import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNewsletterSubscribe } from 'src/hooks';
import { AlertPing, SimpleSpinner } from 'src/components';

export type NewsletterSubscribeVariant = 'footer' | 'modal' | 'card';

type Props = {
  focusOnMount?: boolean;
  variant?: NewsletterSubscribeVariant;
};

export const NewsletterSubscribeForm = ({
  focusOnMount = false,
  variant = 'footer',
}: Props) => {
  const [email, setEmail] = useState<string>('');

  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const newsletterSubscribe = useNewsletterSubscribe({ setEmail, inputRef });

  const isFooter = variant === 'footer';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    newsletterSubscribe.mutate(email);
  };

  useEffect(() => {
    if (focusOnMount) {
      inputRef.current?.focus();
    }
  }, [focusOnMount]);

  const statusPing = newsletterSubscribe.isError ? (
    <AlertPing variant='error' customX='-right-2' customY='-top-2' />
  ) : newsletterSubscribe.isPending ? (
    <SimpleSpinner customX='-right-2' customY='-top-2' />
  ) : null;

  const sharedInputProps = {
    type: 'email',
    required: true,
    placeholder: t('footer.your_email'),
    value: email,
    ref: inputRef,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
    onBlur: () => {
      if (newsletterSubscribe.isError) {
        newsletterSubscribe.reset();
      }
    },
    disabled: newsletterSubscribe.isPending,
  };

  // Stacked field and full-width CTA for the dark DealsNewsletterCard surface.
  if (variant === 'card') {
    return (
      <form onSubmit={handleSubmit} className='flex w-full flex-col gap-3'>
        <div className='relative'>
          <input
            id='deals-card-newsletter-email'
            aria-label={t('footer.your_email')}
            {...sharedInputProps}
            className={`h-11 w-full rounded-lg border-2 px-3 font-secondary text-sm text-primary-dark outline-none transition placeholder:text-darkGray disabled:opacity-60 ${
              newsletterSubscribe.isError
                ? 'border-rose-500 bg-rose-100'
                : 'border-transparent bg-offWhite focus:border-customYellow'
            }`}
          />
          {statusPing}
        </div>

        <button
          type='submit'
          disabled={newsletterSubscribe.isPending}
          data-attr='deals-card-newsletter-subscribe'
          className='h-11 w-full rounded-full bg-customYellow font-primary text-sm font-bold text-primary-dark transition-all hover:opacity-90 active:scale-95 disabled:opacity-60'
        >
          {t('deals.newsletter_card_cta')}
        </button>
      </form>
    );
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className={`mx-auto my-2 flex w-full flex-col items-center justify-start gap-x-2.5 gap-y-2 ${isFooter ? 'max-w-[420px] md:mx-0 md:flex-row lg:max-w-full' : 'sm:mx-0 sm:flex-row lg:justify-center'}`}
      >
        <div
          className={`group relative h-10 w-full min-w-[200px] lg:w-[260px] lg:grow xl:w-[300px]`}
        >
          <input
            {...sharedInputProps}
            className={`h-10 w-full min-w-[200px] rounded border-none px-3 py-2 font-secondary font-semibold leading-[24px] text-primary-dark outline-none ring-2 transition-all selection:bg-primary selection:text-cloudGray placeholder:font-normal placeholder:text-darkGray focus:ring-2 disabled:bg-gray-300 disabled:opacity-60 disabled:ring-primary ${isFooter ? 'lg:w-[260px] xl:w-[300px]' : 'lg:grow'} ${
              newsletterSubscribe.isError
                ? 'bg-rose-100 ring-rose-500 hover:bg-rose-50 focus:hover:bg-rose-100'
                : newsletterSubscribe.isPending
                  ? 'focus:ring-primary'
                  : `${isFooter ? 'bg-cloudGray hover:bg-gray-200 focus:hover:bg-cloudGray' : 'bg-offWhite'} ring-transparent focus:ring-primary`
            }`}
          />

          {statusPing}
        </div>

        <button
          className={`button-secondary h-10 w-full bg-white disabled:opacity-60 ${isFooter ? 'md:w-fit' : 'sm:w-fit'}`}
          type='submit'
          disabled={newsletterSubscribe.isPending}
        >
          {t('footer.subscribe')}
        </button>
      </form>
      <span
        className={`text-100 w-full text-pretty text-center ${isFooter ? 'md:max-w-[440px] md:text-left' : ''}`}
      >
        {t('footer.never_spam')}
      </span>
    </>
  );
};
